import { useEffect, useState } from "react";
import axios from "axios";
import {
  MessageCircle,
  Phone,
  CalendarDays,
  Check,
  Clock,
  Bell,
  MapPin,
  ExternalLink,
  Ticket,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Reveal } from "@/components/Reveal";
import { Link } from "react-router-dom";
import { CONTACT, EVENT_FORMATS, IMAGES, API_BASE, SITE_URL, formatEventDate } from "@/content";
import { Seo } from "@/components/Seo";
import { JsonLd } from "@/components/JsonLd";

const storePlace = {
  "@type": "Place",
  name: "Shibuya Anime & TCG Store",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Zülpicherstr. 357",
    postalCode: "50935",
    addressLocality: "Köln",
    addressCountry: "DE",
  },
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API_BASE}/events`)
      .then((res) => setEvents(Array.isArray(res.data) ? res.data : []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  const eventsSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Event",
        name: "Cologne Card Con 2026",
        startDate: "2026-09-06T10:00",
        endDate: "2026-09-06T18:00",
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        eventStatus: "https://schema.org/EventScheduled",
        location: {
          "@type": "Place",
          name: "Halle Tor 2",
          address: { "@type": "PostalAddress", streetAddress: "Girlitzweg 30", addressLocality: "Köln", addressCountry: "DE" },
        },
        description: "Kölns große Messe für Sammelkarten & Sportkarten: 80+ Aussteller, Live Breaks, Trading-Lounges und On-Site Grading.",
        organizer: { "@type": "Organization", name: "Cologne Card Con", url: "https://cologne-card-con.de" },
        url: "https://cologne-card-con.de",
      },
      ...events.map((e) => ({
        "@type": "Event",
        name: e.title,
        startDate: e.date,
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        eventStatus: "https://schema.org/EventScheduled",
        location: storePlace,
        ...(e.description ? { description: e.description } : {}),
        organizer: { "@type": "Organization", name: "Shibuya Anime & TCG Store", url: SITE_URL },
      })),
    ],
  };

  return (
    <div data-testid="events-page">
      <Seo
        title="TCG Events Köln | Trade Days & Releases bei Shibuya"
        description="Entdecke TCG Events, Trade Days, Release Days und Community-Treffen im Shibuya Anime & TCG Store Köln. Aktuelle Termine auf der Zülpicher Str. 357."
        path="/events"
        keywords="TCG Events Köln, Trade Day Köln, Pokémon TCG Köln, One Piece TCG Köln, Release Day Köln, Karten tauschen Köln, TCG Turnier Köln"
      />
      <JsonLd id="events-schema" data={eventsSchema} />
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0">
          <img
            src={IMAGES.tokyoNeonStreet}
            alt=""
            aria-hidden="true"
            className="kenburns h-full w-full object-cover"
            loading="eager"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, hsl(20 14% 4.5% / 0.96) 0%, hsl(20 14% 4.5% / 0.85) 45%, hsl(18 30% 8% / 0.7) 100%)",
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, transparent 50%, hsl(20 14% 4.5%) 100%)",
            }}
          />
          <div className="hero-glow absolute inset-0" />
        </div>
        <div
          aria-hidden="true"
          className="neon-flicker pointer-events-none absolute right-8 top-1/2 z-10 hidden -translate-y-1/2 select-none lg:block"
        >
          <p className="neon-kanji kanji-vertical text-4xl">イベント</p>
        </div>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-6 top-0 select-none font-jp text-[140px] font-black leading-none text-white/[0.05] md:text-[220px]"
        >
          祭
        </div>
        <div className="relative z-10 mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
          <Reveal>
            <Badge
              variant="outline"
              className="mb-6 rounded-full border-accent/40 bg-accent/10 px-4 py-1.5 font-mono-ui text-[11px] uppercase tracking-widest text-accent backdrop-blur-sm"
            >
              <CalendarDays className="mr-1.5 h-3 w-3" />
              Community Events
            </Badge>
            <h1 className="max-w-3xl font-display text-5xl uppercase leading-[0.95] tracking-[0.04em] sm:text-6xl lg:text-7xl">
              TCG Events, Trade Days
              <br />
              &amp; <span className="neon-text-red">Releases in Köln.</span>
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
              Shibuya ist mehr als ein Store – es ist ein Treffpunkt. Hier wird
              getauscht, gespielt und gefeiert.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============ COLOGNE CARD CON HIGHLIGHT ============ */}
      <section className="mx-auto max-w-6xl px-4 pt-16 sm:px-6 md:pt-24">
        <Reveal>
          <div
            data-testid="cologne-card-con-banner"
            className="neon-card noise relative overflow-hidden rounded-2xl border border-accent/40 bg-card"
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -right-4 -top-8 select-none font-jp text-[160px] font-black leading-none text-accent/[0.06]"
            >
              祭
            </div>
            <div className="relative z-10 p-6 md:p-10">
              <div className="flex flex-wrap items-center gap-3">
                <Badge className="rounded-full bg-accent/15 font-mono-ui text-[10px] uppercase tracking-wider text-accent">
                  <Sparkles className="mr-1.5 h-3 w-3" />
                  Messe-Highlight 2026
                </Badge>
              </div>

              <div className="mt-6 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
                <div className="max-w-2xl">
                  <p className="font-display text-4xl uppercase leading-[0.95] tracking-[0.04em] sm:text-5xl">
                    Cologne <span className="neon-text-red">Card Con.</span>
                  </p>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-base">
                    Kölns große Messe für Sammelkarten & Sportkarten: 80+ Aussteller,
                    Live Breaks, Trading-Lounges und On-Site Grading – alles an einem
                    Tag, an einem Ort. Tickets gibt es online oder direkt bei uns im Store.
                  </p>
                  <div className="mt-5 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:gap-5">
                    <p className="flex items-center gap-2 font-mono-ui text-xs uppercase tracking-wider text-foreground">
                      <CalendarDays className="h-4 w-4 text-accent" />
                      So, 06. September 2026
                    </p>
                    <p className="flex items-center gap-2 font-mono-ui text-xs uppercase tracking-wider text-foreground">
                      <Clock className="h-4 w-4 text-accent" />
                      10:00–18:00 Uhr
                    </p>
                    <p className="flex items-center gap-2 font-mono-ui text-xs uppercase tracking-wider text-foreground">
                      <MapPin className="h-4 w-4 text-accent" />
                      Halle Tor 2 · Girlitzweg 30, Köln
                    </p>
                  </div>
                  <p className="mt-3 font-mono-ui text-[11px] uppercase tracking-wider text-muted-foreground">
                    Pokémon · Magic · One Piece · Yu-Gi-Oh · NBA · NFL · Soccer
                  </p>
                </div>

                <div className="grid shrink-0 grid-cols-3 gap-6 lg:gap-8">
                  {[
                    { value: "80+", label: "Aussteller" },
                    { value: "3.500", label: "m² Fläche" },
                    { value: "5.000", label: "Besucher" },
                  ].map((stat) => (
                    <div key={stat.label}>
                      <p className="font-display text-3xl text-accent md:text-4xl">{stat.value}</p>
                      <p className="mt-1 font-mono-ui text-[10px] uppercase tracking-wider text-muted-foreground">
                        {stat.label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  asChild
                  data-testid="card-con-tickets-button"
                  className="h-11 rounded-xl bg-accent px-5 font-medium text-accent-foreground shadow-[0_10px_30px_hsl(0_85%_55%/0.3)] transition-shadow hover:shadow-[0_14px_40px_hsl(0_85%_55%/0.45)]"
                >
                  <a href="https://cologne-card-con.de/tickets" target="_blank" rel="noopener noreferrer">
                    <Ticket className="mr-2 h-4 w-4" /> Tickets sichern
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  data-testid="card-con-website-button"
                  className="h-11 rounded-xl border-border bg-transparent px-5 hover:bg-accent/10"
                >
                  <a href="https://cologne-card-con.de" target="_blank" rel="noopener noreferrer">
                    cologne-card-con.de <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ============ AKTUELLE TERMINE ============ */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
        <Reveal>
          <div className="flex items-center gap-3">
            <p className="font-mono-ui text-xs uppercase tracking-[0.25em] text-accent">
              Aktuelle Termine
            </p>
            <span aria-hidden="true" className="neon-kanji text-sm">予定</span>
          </div>
          <h2 className="mt-3 font-display text-4xl uppercase tracking-[0.04em] sm:text-5xl">
            Nächste <span className="neon-text-red">Events.</span>
          </h2>
        </Reveal>

        <div data-testid="events-upcoming" className="mt-10">
          {loading ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-44 rounded-2xl bg-card" />
              ))}
            </div>
          ) : events.length > 0 ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {events.map((event, i) => (
                <Reveal key={event.id} delay={i * 0.06}>
                  <div
                    data-testid={`upcoming-event-${event.id}`}
                    className="neon-card noise relative h-full overflow-hidden rounded-2xl border border-primary/30 bg-card p-6"
                  >
                    <div className="relative z-10">
                      <div className="flex items-center justify-between gap-3">
                        <Badge className="rounded-full bg-primary/15 font-mono-ui text-[10px] uppercase tracking-wider text-primary">
                          {event.tag}
                        </Badge>
                        <span className="font-display text-xl text-accent">
                          {formatEventDate(event.date)}
                        </span>
                      </div>
                      <p className="mt-4 font-display text-2xl uppercase leading-tight tracking-wide">
                        {event.title}
                      </p>
                      {event.time_text && (
                        <p className="mt-1.5 flex items-center gap-1.5 font-mono-ui text-[11px] uppercase tracking-wider text-muted-foreground">
                          <Clock className="h-3.5 w-3.5" /> {event.time_text}
                        </p>
                      )}
                      {event.description && (
                        <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
                          {event.description}
                        </p>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          ) : (
            <Alert
              data-testid="events-empty-state"
              className="border-primary/30 bg-card"
            >
              <Bell className="h-4 w-4 text-primary" />
              <AlertTitle className="text-sm font-medium">
                Die nächsten Termine werden gerade geplant
              </AlertTitle>
              <AlertDescription className="text-xs leading-relaxed text-muted-foreground">
                Ruf uns einfach kurz an – dann erfährst du sofort, wann der
                nächste Trade Day oder ein Event ansteht. Neue Termine erscheinen
                hier, sobald sie feststehen.
              </AlertDescription>
            </Alert>
          )}
        </div>
      </section>

      {/* ============ EVENT-FORMATE ============ */}
      <section className="relative overflow-hidden border-t border-border bg-card/50">
        <div aria-hidden="true" className="speedlines pointer-events-none absolute inset-0 opacity-60" />
        <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
          <Reveal>
            <div className="flex items-center gap-3">
              <p className="font-mono-ui text-xs uppercase tracking-[0.25em] text-primary">
                Unsere Event-Formate
              </p>
              <span aria-hidden="true" className="neon-kanji-orange text-sm">形式</span>
            </div>
            <h2 className="mt-3 font-display text-4xl uppercase tracking-[0.04em] sm:text-5xl">
              Das erwartet <span className="neon-text">dich.</span>
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
            {EVENT_FORMATS.map((event, i) => (
              <Reveal key={event.id} delay={i * 0.06}>
                <div
                  data-testid={`event-card-${event.id}`}
                  className="neon-card noise manga-frame relative h-full overflow-hidden rounded-2xl border border-border bg-card p-6 md:p-8"
                >
                  <span aria-hidden="true" className="neon-kanji-orange absolute right-5 top-5 text-2xl opacity-60">
                    {event.kanji}
                  </span>
                  <div className="relative z-10">
                    <Badge
                      className={`rounded-full font-mono-ui text-[10px] uppercase tracking-wider ${
                        event.accent === "accent"
                          ? "bg-accent/15 text-accent"
                          : "bg-primary/15 text-primary"
                      }`}
                    >
                      {event.tag}
                    </Badge>
                    <p className="mt-5 font-display text-3xl uppercase leading-tight tracking-wide">
                      {event.title}
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      {event.description}
                    </p>
                    <ul className="mt-5 space-y-2.5">
                      {event.highlights.map((highlight) => (
                        <li key={highlight} className="flex items-start gap-2.5 text-sm">
                          <span
                            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                              event.accent === "accent" ? "bg-accent/15" : "bg-primary/15"
                            }`}
                          >
                            <Check
                              className={`h-3 w-3 ${
                                event.accent === "accent" ? "text-accent" : "text-primary"
                              }`}
                            />
                          </span>
                          <span className="text-muted-foreground">{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ INTERNE LINKS ============ */}
      <section className="mx-auto max-w-6xl px-4 pt-4 sm:px-6">
        <Reveal>
          <div className="flex flex-wrap gap-3">
            {[
              { to: "/pokemon-karten-koeln", label: "Pokémon Karten Köln" },
              { to: "/one-piece-karten-koeln", label: "One Piece Karten Köln" },
              { to: "/tcg-zubehoer-koeln", label: "TCG Zubehör Köln" },
              { to: "/kontakt", label: "Kontakt" },
            ].map((r) => (
              <Link key={r.to} to={r.to} data-testid={`events-related-${r.to}`} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground">
                {r.label}
              </Link>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ============ CTA STRIP ============ */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
          <Reveal>
            <div className="noise relative overflow-hidden rounded-2xl border border-border bg-card p-8 text-center md:p-14">
              <div className="cta-glow pointer-events-none absolute inset-0" />
              <div className="relative z-10">
                <h2 className="font-display text-4xl uppercase tracking-[0.04em] sm:text-5xl">
                  Beim nächsten Event <span className="neon-text">dabei sein?</span>
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground md:text-base">
                  Ruf uns an und wir sagen dir sofort, wann der nächste Trade Day
                  oder ein Meetup ansteht.
                </p>
                <div className="mt-8 flex flex-wrap justify-center gap-3">
                  <Button
                    asChild
                    size="lg"
                    data-testid="events-cta-phone-button"
                    className="h-12 rounded-xl bg-primary px-6 font-medium text-primary-foreground shadow-[0_10px_30px_hsl(22_100%_50%/0.35)] transition-shadow hover:shadow-[0_14px_44px_hsl(22_100%_50%/0.5)]"
                  >
                    <a href={CONTACT.phoneHref}>
                      <Phone className="mr-2 h-5 w-5" /> Jetzt anrufen
                    </a>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    data-testid="events-cta-whatsapp-button"
                    className="h-12 rounded-xl border-primary/30 bg-transparent px-6 hover:bg-primary/10"
                  >
                    <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="mr-2 h-5 w-5" /> WhatsApp
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
